import { clsx } from "clsx"
import { Link } from "react-router-dom"

import type { SolarShareReadinessDto } from "~/api/dto/solar-presentation.dto"
import {
  useExportSolarPdf,
  useSolarShareState,
} from "~/api/hooks/useSolarPresentationShare"
import { solarShareBlockedReason } from "~/api/solar-presentation-errors"
import {
  type PdfExportStart,
  usePdfExportTab,
} from "~/containers/ProposalSetup/share/use-pdf-export-tab"

import {
  SOLAR_EXPORT_CHECKING_HINT,
  solarBlockerCopy,
  SOLAR_EXPORT_CONFLICT_PROMPT,
  SOLAR_EXPORT_NOT_READY_COPY,
  SOLAR_EXPORT_READINESS_FAILED_PROMPT,
} from "./solar-share-copy"
import { solarShareFixPath } from "../solar-urls"

export const SOLAR_EXPORT_PDF_LABEL = `Export To PDF`
export const SOLAR_EXPORTING_LABEL = `Generating PDF`
export const SOLAR_EXPORT_FAILED_PROMPT = `Unable to generate the PDF.`
export const SOLAR_EXPORT_RETRY_LABEL = `Try again`
export const SOLAR_EXPORT_POPUP_BLOCKED_PROMPT = `Allow pop-ups for this site, then export again.`
export const SOLAR_EXPORT_TAB_CLOSED_PROMPT = `The tab was closed before the PDF was ready. Export again to open it.`

export type SolarExportPdfAction = {
  /** Why export is unavailable now, from the server's readiness; null when ready. */
  readonly notReady: Extract<SolarShareReadinessDto, { ready: false }> | null
  /** Readiness hasn't answered yet, so export waits for it. */
  readonly checking: boolean
  /** The readiness read gave up without an answer; export waits for a retry. */
  readonly readinessFailed: boolean
  readonly retryReadiness: () => void
  /**
   * Why export can't start now, for quick search: not ready, still checking,
   * readiness unavailable, or already generating. Null when it can start.
   */
  readonly lockedHint: string | null
  readonly conflict: boolean
  readonly failed: boolean
  readonly isLoading: boolean
  readonly popupBlocked: boolean
  readonly start: () => PdfExportStart | `locked`
  readonly tabClosed: boolean
}

/**
 * Export To PDF for a Solar Proposal: one continuous page of the presentation,
 * opened in a new tab. Disabled with its reason while the server's readiness
 * says pricing or savings is not current; a refusal that lost a race to a
 * change mid-render reads as "Pricing changed". The rail and quick search share
 * one instance.
 */
export function useSolarExportPdfAction(
  proposalId: string,
  enabled: boolean,
): SolarExportPdfAction {
  const share = useSolarShareState(enabled ? proposalId : undefined)
  const exportTab = usePdfExportTab(useExportSolarPdf(), `proposal`)
  const readiness = share.data?.readiness
  const notReady = readiness && !readiness.ready ? readiness : null
  const conflict = solarShareBlockedReason(exportTab.error) != null
  // A settled failure with nothing cached is not "checking": it needs a retry.
  // A retry in flight reads as checking again.
  const readinessFailed =
    share.data == null && share.isError && !share.isFetching
  const checking = share.data == null && !readinessFailed
  const lockedHint = exportTab.isLoading
    ? SOLAR_EXPORTING_LABEL
    : notReady
      ? SOLAR_EXPORT_NOT_READY_COPY[notReady.reason]
      : readinessFailed
        ? SOLAR_EXPORT_READINESS_FAILED_PROMPT
        : checking
          ? SOLAR_EXPORT_CHECKING_HINT
          : null

  return {
    notReady,
    checking,
    readinessFailed,
    retryReadiness: () => {
      void share.refetch()
    },
    lockedHint,
    conflict,
    failed: exportTab.isError && !conflict,
    isLoading: exportTab.isLoading,
    popupBlocked: exportTab.popupBlocked,
    // One render at a time, and never before the server says it is ready.
    start: () =>
      lockedHint != null ? `locked` : exportTab.start({ proposalId }),
    tabClosed: exportTab.tabClosed,
  }
}

export function SolarExportPdfActionRow({
  action,
  proposalId,
}: {
  action: SolarExportPdfAction
  proposalId: string
}): JSX.Element {
  const {
    checking,
    conflict,
    failed,
    isLoading,
    notReady,
    popupBlocked,
    readinessFailed,
    retryReadiness,
    start,
    tabClosed,
  } = action
  const disabled = isLoading || checking || readinessFailed || notReady != null
  const firstBlocker = notReady?.blockers[0]

  return (
    // Matches the rail's part rows, so Export sits level with Share.
    <div className="mx-[5px] flex flex-col gap-1 p-[7px]">
      <button
        type="button"
        data-testid="solar-setup-part-export-pdf"
        disabled={disabled}
        onClick={() => {
          start()
        }}
        className={clsx(
          `font-display w-full text-left text-[0.875rem] leading-[21px] font-medium`,
          isLoading || checking
            ? `cursor-progress text-bp-text-dim`
            : notReady || readinessFailed
              ? `cursor-not-allowed text-bp-text-dim`
              : `text-bp-text`,
        )}
      >
        {isLoading ? SOLAR_EXPORTING_LABEL : SOLAR_EXPORT_PDF_LABEL}
      </button>
      {notReady ? (
        <div className="flex flex-col items-start gap-1">
          <p
            role="status"
            data-testid="solar-export-not-ready"
            className="text-[0.75rem] leading-tight text-bp-gold"
          >
            {SOLAR_EXPORT_NOT_READY_COPY[notReady.reason]}
          </p>
          {firstBlocker ? (
            <Link
              to={solarShareFixPath(proposalId, firstBlocker.step)}
              className="text-[0.75rem] leading-tight font-semibold text-bp-accent underline underline-offset-2"
            >
              {solarBlockerCopy(firstBlocker.code, firstBlocker.step).fixLabel}
            </Link>
          ) : null}
        </div>
      ) : null}
      {readinessFailed ? (
        <div className="flex flex-col items-start gap-1">
          <p
            role="alert"
            className="text-[0.75rem] leading-tight text-bp-danger"
          >
            {SOLAR_EXPORT_READINESS_FAILED_PROMPT}
          </p>
          <button
            type="button"
            onClick={retryReadiness}
            className="text-[0.75rem] leading-tight font-semibold text-bp-accent underline underline-offset-2"
          >
            {SOLAR_EXPORT_RETRY_LABEL}
          </button>
        </div>
      ) : null}
      {conflict ? (
        <p role="alert" className="text-[0.75rem] leading-tight text-bp-gold">
          {SOLAR_EXPORT_CONFLICT_PROMPT}
        </p>
      ) : null}
      {popupBlocked ? (
        <p role="alert" className="text-[0.75rem] leading-tight text-bp-danger">
          {SOLAR_EXPORT_POPUP_BLOCKED_PROMPT}
        </p>
      ) : null}
      {tabClosed ? (
        <p role="alert" className="text-[0.75rem] leading-tight text-bp-danger">
          {SOLAR_EXPORT_TAB_CLOSED_PROMPT}
        </p>
      ) : null}
      {failed ? (
        <div className="flex flex-col items-start gap-1">
          <p
            role="alert"
            className="text-[0.75rem] leading-tight text-bp-danger"
          >
            {SOLAR_EXPORT_FAILED_PROMPT}
          </p>
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              start()
            }}
            className="text-[0.75rem] leading-tight font-semibold text-bp-accent underline underline-offset-2 disabled:cursor-progress disabled:text-bp-text-dim"
          >
            {SOLAR_EXPORT_RETRY_LABEL}
          </button>
        </div>
      ) : null}
    </div>
  )
}
